import * as React from "react";
import { Guid } from "../Utils/Guid";
import { Tooltip } from "./Components/Tooltip";
import { ValidationErrorsContainer } from "./ValidationErrorsContainer";
import { Position } from "../Enums/Position";
import AriaTooltip from "../../applications/Shared/Tooltip/AriaTooltip";
import { CommonHelper } from "../Helpers/CommonHelper";

export interface IBaseEditorProps {
    label?: string;
    labelGridClasses?: string;
    cssPercentageOrMoneyClasses?: string;
    tooltip?: string;
    tooltipPosition?: Position;
    tooltipCss?: string;
    showTooltipCloseButton?: boolean;
    inputGridClasses?: string;

    isInvalid?: boolean;
    appendErrors?: string[];
    errorsWrapperCss?: string;
    cssWrapper?: string;

    // Use this if want to display the validation messages outside the input component
    onValidation?: (validationMessages: string[]) => void;

    infoText?: string;
    infoTextGridClasses?: string;
    runValidationOnMount?: boolean;
    cssControlTxt?: string;

    isNotForm?: boolean;
}

export interface IEditorState {
    isValid?: boolean;
    isValidOutside?: boolean;
    typingFinished?: boolean;
    validationErrors?: string[];
    inputGroupId?: string;
}

export abstract class BaseEditor<TProps extends IBaseEditorProps> extends React.Component<TProps, IEditorState> {
    public _infoTextId: string = Guid.getNext();
    public _errorMessageId: string = Guid.getNext();
    protected wrapperRef: HTMLElement;

    constructor(props: TProps) {
        super(props);
        this.state = this.getDefaultState();
    }

    public abstract render(): JSX.Element;

    public UNSAFE_componentWillReceiveProps(newProps: IBaseEditorProps): void {
        this.setState({
            isValidOutside: !(newProps.appendErrors && newProps.appendErrors.length > 0) && !newProps.isInvalid,
        });
    }

    protected getDefaultState(): IEditorState {
        return {
            isValid: true,
            isValidOutside: !(this.props.appendErrors && this.props.appendErrors.length > 0) && !this.props.isInvalid,
            typingFinished: false,
            validationErrors: new Array<string>(),
        };
    }

    protected getTooltip(): JSX.Element {
        if (!this.props.tooltip || !this.props.tooltip.length) {
            return null;
        }

        return (
            <AriaTooltip
                showCloseButton={this.props?.showTooltipCloseButton}
                cssWrapper={
                    !CommonHelper.isNullOrEmpty(this.props?.tooltipCss) ? this.props?.tooltipCss : "is-info-icon ml-5"
                }
            >
                {this.props.tooltip}
            </AriaTooltip>
        );
    }

    protected onValidationResultChange(
        isValid: boolean,
        validationErrors: string[],
        showValidationMessages?: boolean
    ): void {
        this.setState({
            isValid,
            validationErrors,
            typingFinished: showValidationMessages ? true : this.state.typingFinished,
        });
    }

    protected getValidationErrorsContainer(): JSX.Element {
        const errors =
            this.state.validationErrors && this.props.appendErrors
                ? [...this.state.validationErrors, ...this.props.appendErrors]
                : this.state.validationErrors;

        return (
            <ValidationErrorsContainer
                hide={!!this.props.onValidation}
                validationErrors={errors}
                id={this._errorMessageId}
                errorsWrapperCss={this.props.errorsWrapperCss}
            />
        );
    }

    protected getInfoText(): JSX.Element {
        if (!this.props.infoText) {
            return null;
        }

        if (this.props.infoTextGridClasses) {
            return (
                <div className="row">
                    <div className="col-12">
                        <div className={this.props.infoTextGridClasses}>
                            <p
                                className="text-field-info"
                                id={this._infoTextId}
                                dangerouslySetInnerHTML={{ __html: this.props.infoText }}
                            />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <p
                className="text-field-info"
                id={this._infoTextId}
                dangerouslySetInnerHTML={{ __html: this.props.infoText }}
            />
        );
    }
}
